// ClinicalHistory.jsx
import s from '../shared.module.css';
import c from './clinicalhistory.module.css';
import PatientHeader from '../PatientHeader';
import HealthTimeline from '../HealthTimeline';
import CarePlanSection from '../CarePlanSection';

const clinicalTimeline = [
  { icon: '👧', label: 'Menarche', sub: 'Age 13', done: true },
  { icon: '📋', label: 'Irregular Cycles', sub: 'Age 19', done: true },
  { icon: '⚕️', label: 'PCOS Diagnosed', sub: '2020', done: true },
  { icon: '💊', label: 'Started Metformin', sub: '2021', done: true },
  { icon: '💜', label: 'Fertility Planning', sub: '2024', done: true },
  { icon: '🌸', label: 'Preconception Care', sub: '2026', active: true },
];

export default function ClinicalHistory() {
  return (
    <div className={s.pageWrap}>
      <div className={s.pageHead}>
        <div className={s.pageHeadLeft}>
          <h1 className={s.pageTitle}>Clinical History</h1>
          <p className={s.pageSubtitle}>Longitudinal clinical context, conditions, interventions, and family history across the patient's care journey.</p>
        </div>
        <div className={s.pageHeadActions}>
          <button className={s.btnOutline}>✏️ Update History</button>
          <button className={s.btnPrimary}>+ Add Note</button>
        </div>
      </div>

      <PatientHeader
        summaryFields={[
          { groupLabel: 'Clinical Summary', label: 'BMI', value: '25.9 (Overweight)' },
          { label: 'Last Visit', value: '12 May 2026' },
          { label: 'Primary Physician', value: 'Dr. Ananya Sharma' },
        ]}
      />

      {/* Quick stats bar */}
      <div className={c.quickStatsBar}>
        {[
          { icon: '❤️', title: 'Active Conditions', items: ['PCOS', 'Irregular Periods', 'Vitamin D Deficiency'] },
          { icon: '⚠️', title: 'Allergies', items: ['Penicillin', 'No food allergies'] },
          { icon: '🔬', title: 'Past Procedures', items: ['Pelvic Ultrasound', 'Hormonal Panel'] },
          { icon: '👨‍👩‍👧', title: 'Family History', items: ['Diabetes', 'Thyroid Disorder'] },
          { icon: '🚨', title: 'Risk Context', items: ['Moderate metabolic risk', 'Thyroid screening due'] },
        ].map((q, i) => (
          <div key={i} className={c.quickStatCard}>
            <div className={c.quickStatIcon}>{q.icon}</div>
            <div className={c.quickStatBody}>
              <p className={c.quickStatTitle}>{q.title}</p>
              <div className={c.quickStatList}>
                {q.items.map((item, j) => (
                  <span key={j} className={c.quickStatItem}>• {item}</span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Clinical Journey Timeline */}
      <div className={s.card} style={{ marginBottom: 16 }}>
        <h2 className={s.sectionTitle} style={{ marginBottom: 14 }}>Clinical Journey Timeline</h2>
        <HealthTimeline events={clinicalTimeline} />
      </div>

      {/* Problem list + Medications */}
      <div className={s.twoCol} style={{ marginBottom: 16 }}>
        <div className={s.leftCol}>
          {/* Problem list */}
          <div className={s.card}>
            <h2 className={s.sectionTitle} style={{ marginBottom: 14 }}>Problem List & Medical History</h2>
            <div className={c.problemGrid}>
              <div className={c.problemSection}>
                {[
                  { icon: '❤️', title: 'Active Conditions', items: ['PCOS — Moderate', 'Irregular Menstrual Cycles', 'Vitamin D Deficiency', 'Anemia (resolved)', 'Acne', 'Mild insulin resistance'] },
                  { icon: '📝', title: 'Past Medical History', items: [] },
                  { icon: '✂️', title: 'Surgical / Procedure History', items: ['No major surgery', 'Pelvic ultrasound completed', 'Hormonal investigations completed'] },
                  { icon: '🏥', title: 'Hospitalizations', items: ['None reported'] },
                ].map((sec, i) => (
                  <div key={i} className={c.problemRow}>
                    <div className={c.problemIcon}>{sec.icon}</div>
                    <div className={c.problemBody}>
                      <p className={c.problemTitle}>{sec.title}</p>
                      <div className={c.problemItems}>
                        {sec.items.length > 0 ? sec.items.map((item, j) => (
                          <div key={j} className={c.problemItem}>
                            <span className={c.problemBulletPurple} />
                            {item}
                            {item.includes('Moderate') && <span className={s.badgeOrange} style={{ marginLeft: 4 }}>Moderate</span>}
                          </div>
                        )) : <span className={c.noneText}>No records</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className={c.problemSection}>
                <div className={c.problemRow}>
                  <div className={c.problemIcon}>🔬</div>
                  <div className={c.problemBody}>
                    <p className={c.problemTitle}>Gynecological History</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px', marginTop: 4 }}>
                      {[
                        { k: 'Menarche', v: 'Age 13' },
                        { k: 'Regularity', v: 'Irregular (28–35 days)' },
                        { k: 'Dysmenorrhea', v: 'Mild' },
                        { k: 'Sexually Active', v: 'Yes' },
                      ].map((r, i) => (
                        <div key={i} style={{ fontSize: 11, padding: '2px 0' }}>
                          <span style={{ color: 'var(--ava-color-caption)' }}>{r.k}: </span>
                          <span style={{ color: 'var(--ava-color-heading)', fontWeight: 600 }}>{r.v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className={c.problemRow}>
                  <div className={c.problemIcon}>🤰</div>
                  <div className={c.problemBody}>
                    <p className={c.problemTitle}>Obstetric History</p>
                    <p style={{ fontSize: 11, color: 'var(--ava-color-heading)', fontWeight: 600 }}>G0 P0</p>
                    <p style={{ fontSize: 10, color: 'var(--ava-color-caption)' }}>No prior pregnancies</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Clinical Insights */}
          <div className={s.card}>
            <h2 className={s.sectionTitle} style={{ marginBottom: 14 }}>Clinical Insights</h2>
            <div className={c.insightBar}>
              <div className={`${c.insightBox} ${c.insightBoxRed}`}>
                <p className={`${c.insightBoxTitle} ${c.insightBoxTitleRed}`}>⚠️ Key Clinical Alerts</p>
                {['Irregular cycles persist', 'Low vitamin D', 'Possible metabolic risk'].map((a, i) => (
                  <div key={i} className={c.insightBoxItem}>• {a}</div>
                ))}
              </div>
              <div className={`${c.insightBox} ${c.insightBoxPurple}`}>
                <p className={`${c.insightBoxTitle} ${c.insightBoxTitlePurple}`}>📋 Recommended Evaluations</p>
                {['TSH, HbA1c', 'AMH', 'Pelvic ultrasound if indicated'].map((a, i) => (
                  <div key={i} className={c.insightBoxItem}>• {a}</div>
                ))}
              </div>
              <div className={`${c.insightBox} ${c.insightBoxOrange}`}>
                <p className={`${c.insightBoxTitle} ${c.insightBoxTitleOrange}`}>📅 Care Continuity Gaps</p>
                {['Pap smear due', 'Thyroid profile due'].map((a, i) => (
                  <div key={i} className={c.insightBoxItem}>• {a}</div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className={s.rightCol}>
          {/* Medications */}
          <div className={s.card}>
            <h2 className={s.sectionTitle} style={{ marginBottom: 10 }}>💊 Medications & Allergies</h2>
            <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--ava-color-success-500)', margin: '0 0 8px' }}>Current Medications</p>
            <div className={c.medSection}>
              {[
                { name: 'Metformin 500 mg', dose: '1 tab BD' },
                { name: 'Vitamin D3 60,000 IU', dose: 'Weekly' },
                { name: 'Folic Acid 5 mg', dose: '1 tab OD' },
              ].map((m, i) => (
                <div key={i} className={c.medRow}>
                  <div className={c.medDot} />
                  <span className={c.medName}>{m.name}</span>
                  <span className={c.medDose}>{m.dose}</span>
                </div>
              ))}
              <div className={c.adherenceBadge}>✓ Adherence: Good</div>
            </div>

            <div className={s.divider} />
            <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--ava-color-error-500)', margin: '8px 0' }}>Allergies</p>
            {['Penicillin — rash', 'No known food allergies'].map((a, i) => (
              <div key={i} style={{ fontSize: 11, color: 'var(--ava-color-body)', padding: '2px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: 'var(--ava-color-error-500)' }}>●</span> {a}
              </div>
            ))}
          </div>

          {/* Family history */}
          <div className={s.card}>
            <h2 className={s.sectionTitle} style={{ marginBottom: 10 }}>👨‍👩‍👧 Family History & Genetic Risk</h2>
            <div className={c.familyGrid}>
              {[
                { member: 'Mother', condition: 'Type 2 Diabetes' },
                { member: 'Sister', condition: 'Thyroid Disorder' },
                { member: 'Maternal aunt', condition: 'PCOS symptoms' },
                { member: 'Breast/Ovarian cancer history', condition: 'None reported' },
              ].map((f, i) => (
                <div key={i} className={c.familyRow}>
                  <span className={c.familyMember}>{f.member}</span>
                  <span className={c.familyCondition}>{f.condition}</span>
                </div>
              ))}
            </div>
            <div className={c.noFlagBadge}>✓ No high hereditary cancer flags identified</div>
          </div>

          {/* Recent encounters */}
          <div className={s.card}>
            <h2 className={s.sectionTitle} style={{ marginBottom: 10 }}>Recent Encounters & Provider Notes</h2>
            {[
              { icon: '🩺', date: '12 May 2026', doctor: 'Dr. Ananya Sharma', spec: 'Gynecology', note: 'Routine gynecology review. Cycles irregular; weight concerns.' },
              { icon: '🥗', date: '20 Apr 2026', doctor: 'Ms. Riya Malhotra', spec: 'Nutritionist', note: 'Nutrition counseling completed. Focus on insulin balance diet.' },
              { icon: '💜', date: '05 Feb 2026', doctor: 'Dr. Ananya Sharma', spec: 'Gynecology', note: 'Fertility readiness consult. PCOS management plan discussed.' },
            ].map((enc, i) => (
              <div key={i} className={c.encounterRow}>
                <div className={c.encounterIcon}>{enc.icon}</div>
                <span className={c.encounterDate}>{enc.date}</span>
                <span className={c.encounterDoctor}>{enc.doctor}</span>
                <span className={c.encounterSpec}>{enc.spec}</span>
                <span className={c.encounterNote}>{enc.note}</span>
                <span className={c.encounterChevron}>›</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Care Plan */}
      <div className={s.card} style={{ marginBottom: 16 }}>
        <div className={s.sectionHeader}>
          <h2 className={s.sectionTitle}>Care Plan & Follow-up</h2>
        </div>
        <CarePlanSection
          goals={['Improve cycle regularity', 'Optimize preconception health', 'Manage weight']}
          programs={[
            { name: 'Preconception Care Program', status: 'Active' },
            { name: 'Nutrition & Lifestyle Coaching', status: 'Active' },
          ]}
          tasks={[
            { name: 'Complete TSH test', due: 'Due on 18 May 2026' },
            { name: 'Submit Day-2 hormone panel', due: 'Due on 23 May 2026' },
            { name: 'Follow-up consult', due: 'Due on 01 Jun 2026' },
          ]}
          followup={{ date: '01 Jun 2026, 10:30 AM', doctor: 'Dr. Ananya Sharma', specialty: 'Gynecologist', clinic: "Avexa Women's Health Clinic, Mumbai" }}
        />
      </div>
    </div>
  );
}